import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, forkJoin, map, of, Subscription, switchMap } from 'rxjs';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';

// PrimeNG Modules
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MultiSelectModule } from 'primeng/multiselect';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

// Services and Models
import { PromoPlanService, PromoPlanSearchParams } from '../promo-plan.service';
import { PromoPlanDetail } from '../promo-plan-detail.model';
import { Page } from '../../models/page-wrapper.model';
import { ActivityType } from '../../master-data/activity-type/activity-type.model';
import { ActivityTypeService } from '../../master-data/activity-type/activity-type.service';
import { BrandsService } from '../../master-data/brands/brands.service';
import { SubBrandsService } from '../../master-data/brands/sub-brands/sub-brands.service';
import { Customer, CustomerPickerSummary } from '../../master-data/customers/customers.model';
import { CustomersService } from '../../master-data/customers/customers.service';
import { Brand } from '../../master-data/brands/brands.model';
import { SubBrand } from '../../master-data/brands/sub-brands/sub-brands.model';
import { ChipModule } from 'primeng/chip';
import { InputGroupModule } from 'primeng/inputgroup';
import * as XLSX from 'xlsx';
import { ToolbarModule } from 'primeng/toolbar';
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { EntityDropdownMultiselectPickerComponent } from '../../shared/components/entity-dropdown-multiselect-picker/entity-dropdown-multiselect-picker.component';

@Component({
  selector: 'app-list-promo-plan',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    CalendarModule,
    DropdownModule,
    TagModule,
    ChipModule,
    ToolbarModule,
    InputGroupModule,
    ProgressSpinnerModule,
    ToastModule,
    InputGroupAddonModule,
    IftaLabelModule,
    MultiSelectModule,
    EntityDropdownMultiselectPickerComponent
  ],
  providers: [MessageService],
  templateUrl: './list-promo-plan.component.html',
  styleUrl: './list-promo-plan.component.css'
})
export class ListPromoPlanComponent implements OnInit, OnDestroy {

  @ViewChild('dt') dt!: Table;

  promoPlans: PromoPlanDetail[] = [];
  totalRecords: number = 0;
  isLoading = false;
  isExporting = false;
  private promoPlanSub!: Subscription;

  filterForm: FormGroup;

  statusOptions: string[];
  customerPickerAdapter!: EntityDropdownPickerAdapter<CustomerPickerSummary, Customer>;
  brandPickerAdapter!: EntityDropdownPickerAdapter<Brand>;
  subBrandPickerAdapter!: EntityDropdownPickerAdapter<SubBrand>;
  promoTypePickerAdapter!: EntityDropdownPickerAdapter<ActivityType>;

  startDateOptions = [
    'equal to', 'not equal to', 'greater than or equal to', 'less than or equal to', 'is between'
  ];
  endDateOptions = [
    'equal to', 'not equal to', 'greater than or equal to', 'less than or equal to', 'is between'
  ];

  constructor(
    private promoPlanService: PromoPlanService,
    private router: Router,
    private activityTypeService: ActivityTypeService,
    private route: ActivatedRoute,
    private brandsService: BrandsService,
    private subBrandsService: SubBrandsService,
    private customerService: CustomersService,
    private messageService: MessageService,
    private fb: FormBuilder
  ) {
    this.filterForm = this.fb.group({
      id: [''],
      promoDescription: [''],
      customers: [[]],
      brands: [[]],
      subBrands: [[]],
      cities: [[]],
      discountPercentage: [null],
      statuses: [[]],
      batchNumbers: [[]],
      promoTypes: [[]],
      startDateFrom: [null],
      startDateTo: [null],
      endDateFrom: [null],
      endDateTo: [null],
      channels: [[]],
      startDateOption: ['equal to'],
      endDateOption: ['equal to']
    });

    this.statusOptions = ['Draft', 'Changed', 'Settled', 'Cancelled', 'Not Settled'];
  }

  ngOnInit(): void {
    this.customerPickerAdapter = this.customerService.createCustomerPickerAdapter();
    this.brandPickerAdapter = this.brandsService.createBrandPickerAdapter();
    this.subBrandPickerAdapter = this.subBrandsService.createSubBrandPickerAdapter();
    this.promoTypePickerAdapter = this.activityTypeService.createActivityTypePickerAdapter();
  }

  loadPromoPlans(event: TableLazyLoadEvent = {}): void {
    this.isLoading = true;
    const filters = this.filterForm.value;

    const searchParams: PromoPlanSearchParams = {
      promoPlanIds: filters.id ? [filters.id] : undefined,
      customerNumbers: filters.customers?.map((customer: Customer) => customer.customerNumber),
      promoTypes: filters.promoTypes?.map((activityType: ActivityType) => activityType.name),
      statuses: filters.statuses,
      batchNumbers: filters.batchNumbers,
      brands: filters.brands?.map((brand: Brand) => brand.brandName),
      subBrands: filters.subBrands?.map((subBrand: SubBrand) => subBrand.subBrandName),
      cities: filters.cities,
      channels: filters.channels,
      discountPercentage: filters.discountPercentage,
      page: (event.first ?? 0) / (event.rows ?? 25),
      size: event.rows ?? 25,
      sort: this.formatSort(event.sortField, event.sortOrder)
    };

    switch (filters.startDateOption) {
      case 'equal to':
        searchParams.startDateFrom = this.formatDate(filters.startDateFrom);
        searchParams.startDateTo = this.formatDate(filters.startDateFrom);
        break;
      case 'not equal to':
        searchParams.startDateFrom = this.formatDate(new Date(new Date(filters.startDateFrom).setDate(new Date(filters.startDateFrom).getDate() + 1)));
        searchParams.startDateTo = this.formatDate(new Date(new Date(filters.startDateFrom).setDate(new Date(filters.startDateFrom).getDate() - 1)));
        break;
      case 'greater than or equal to':
        searchParams.startDateFrom = this.formatDate(filters.startDateFrom);
        break;
      case 'less than or equal to':
        searchParams.startDateTo = this.formatDate(filters.startDateFrom);
        break;
    }

    switch (filters.endDateOption) {
      case 'equal to':
        searchParams.endDateFrom = this.formatDate(filters.endDateTo);
        searchParams.endDateTo = this.formatDate(filters.endDateTo);
        break;
      case 'not equal to':
        searchParams.endDateFrom = this.formatDate(new Date(new Date(filters.endDateTo).setDate(new Date(filters.endDateTo).getDate() + 1)));
        searchParams.endDateTo = this.formatDate(new Date(new Date(filters.endDateTo).setDate(new Date(filters.endDateTo).getDate() - 1)));
        break;
      case 'greater than or equal to':
        searchParams.endDateFrom = this.formatDate(filters.endDateTo);
        break;
      case 'less than or equal to':
        searchParams.endDateTo = this.formatDate(filters.endDateTo);
        break;
    }

    Object.keys(searchParams).forEach(key => (searchParams[key as keyof PromoPlanSearchParams] == null
      || searchParams[key as keyof PromoPlanSearchParams] === '')
      && delete searchParams[key as keyof PromoPlanSearchParams]);

    this.promoPlanSub = this.promoPlanService.searchPromoPlanDetails(searchParams).subscribe({
      next: (data: Page<PromoPlanDetail>) => {
        this.promoPlans = data.content;
        this.totalRecords = data.totalElements;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load promo plans.' });
        console.error('Error loading promo plans:', err);
      }
    });
  }

  onSearch() {
    this.dt.reset();
  }

  onReset() {
    this.filterForm.reset({
      id: '',
      promoDescription: '',
      customers: [],
      brands: [],
      subBrands: [],
      cities: [],
      discountPercentage: null,
      statuses: [],
      batchNumbers: [],
      promoTypes: [],
      startDateFrom: null,
      startDateTo: null,
      endDateFrom: null,
      endDateTo: null,
      channels: [],
      startDateOption: 'equal to',
      endDateOption: 'equal to'
    });
    this.dt.reset();
  }

  private formatDate(date: Date | null): string | undefined {
    if (!date) {
      return undefined;
    }
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatSort(sortField: string | string[] | undefined | null, sortOrder: number | undefined | null): string | undefined {
    let field = sortField;
    if (Array.isArray(field)) {
      // Not using multi-sort, but good to be safe
      field = field[0];
    }

    if (field) {
      const order = sortOrder === -1 ? 'desc' : 'asc';
      return `${field},${order}`;
    }
    return undefined;
  }

  viewPromoPlan(promoPlanId: number): void {
    this.router.navigate(['.', { outlets: { canvasOutlet: ['promo-plan', 'view', promoPlanId] } }], { relativeTo: this.route.parent?.parent });
  }

  getStatusSeverity(promoPlanDetail: PromoPlanDetail): string {
    switch (promoPlanDetail.promoPlanHeader?.workflowStatus?.statusName) {
      case 'Active':
      case 'Settled':
        return 'success';
      case 'Changed':
      case 'Need Attention':
        return 'warn';
      case 'Cancelled':
        return 'danger';
      case 'Draft':
      default:
        return 'info';
    }
  }

  ngOnDestroy(): void {
    this.promoPlanSub?.unsubscribe();
  }

  uploadPromoPlan(): void {
    // This navigates to the 'add' route within the 'canvasOutlet',
    // assuming a route configuration like { path: 'promo-plan/add', component: ... } for the outlet.
    this.router.navigate(['.', { outlets: { canvasOutlet: ['promo-plan', 'upload'] } }], { relativeTo: this.route.parent?.parent });
  }

  exportExcel(): void {
    const formValues = this.filterForm.value;
    const isFilterFormEmpty = Object.values(formValues).every(value => value === null || value === '' || (Array.isArray(value) && value.length === 0));

    if (isFilterFormEmpty) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Filters Applied',
        detail: 'Please apply at least one filter to export the data.'
      });
      return;
    }

    this.isExporting = true;
    const filters = this.filterForm.value;
    const searchParams: PromoPlanSearchParams = {
      promoPlanIds: filters.id ? [filters.id] : undefined,
      customerNumbers: filters.customers?.map((customer: Customer) => customer.customerNumber),
      promoTypes: filters.promoTypes?.map((activityType: ActivityType) => activityType.name),
      statuses: filters.statuses,
      batchNumbers: filters.batchNumbers,
      brands: filters.brands?.map((brand: Brand) => brand.brandName),
      subBrands: filters.subBrands?.map((subBrand: SubBrand) => subBrand.subBrandName),
      cities: filters.cities,
      channels: filters.channels,
      discountPercentage: filters.discountPercentage,
    };

    switch (filters.startDateOption) {
      case 'equal to':
        searchParams.startDateFrom = this.formatDate(filters.startDateFrom);
        searchParams.startDateTo = this.formatDate(filters.startDateFrom);
        break;
      case 'not equal to':
        searchParams.startDateFrom = this.formatDate(new Date(new Date(filters.startDateFrom).setDate(new Date(filters.startDateFrom).getDate() + 1)));
        searchParams.startDateTo = this.formatDate(new Date(new Date(filters.startDateFrom).setDate(new Date(filters.startDateFrom).getDate() - 1)));
        break;
      case 'greater than or equal to':
        searchParams.startDateFrom = this.formatDate(filters.startDateFrom);
        break;
      case 'less than or equal to':
        searchParams.startDateTo = this.formatDate(filters.startDateFrom);
        break;
    }

    switch (filters.endDateOption) {
      case 'equal to':
        searchParams.endDateFrom = this.formatDate(filters.endDateTo);
        searchParams.endDateTo = this.formatDate(filters.endDateTo);
        break;
      case 'not equal to':
        searchParams.endDateFrom = this.formatDate(new Date(new Date(filters.endDateTo).setDate(new Date(filters.endDateTo).getDate() + 1)));
        searchParams.endDateTo = this.formatDate(new Date(new Date(filters.endDateTo).setDate(new Date(filters.endDateTo).getDate() - 1)));
        break;
      case 'greater than or equal to':
        searchParams.endDateFrom = this.formatDate(filters.endDateTo);
        break;
      case 'less than or equal to':
        searchParams.endDateTo = this.formatDate(filters.endDateTo);
        break;
    }

    // Clean up empty/null parameters
    Object.keys(searchParams).forEach(key =>
      (searchParams[key as keyof PromoPlanSearchParams] == null || searchParams[key as keyof PromoPlanSearchParams] === '') &&
      delete searchParams[key as keyof PromoPlanSearchParams]
    );

    const pageSize = 1000;
    const firstPageParams = { ...searchParams, page: 0, size: pageSize };

    this.promoPlanService.searchPromoPlanDetails(firstPageParams).pipe(
      switchMap((firstPage: Page<PromoPlanDetail>) => {
        const totalPages = firstPage.totalPages;
        if (totalPages <= 1) {
          return of(firstPage.content);
        }

        const pageRequests$ = [];
        for (let i = 1; i < totalPages; i++) {
          const pageParams = { ...searchParams, page: i, size: pageSize };
          pageRequests$.push(this.promoPlanService.searchPromoPlanDetails(pageParams).pipe(map(page => page.content)));
        }

        // Combine the first page content with the rest of the pages
        return forkJoin(pageRequests$).pipe(
          map(otherPagesContents => [firstPage.content, ...otherPagesContents].flat())
        );
      }),
      catchError(err => {
        this.isExporting = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to fetch all data for export.' });
        console.error('Error exporting promo plans:', err);
        return of(null); // Return a safe value to continue the stream
      })
    ).subscribe((allPromoDetails: PromoPlanDetail[] | null) => {
      if (allPromoDetails) {
        const dataToExport = allPromoDetails.map(promo => ({
          'PROMO PLAN ID': promo.promoPlanHeader?.promoPlanId,
          'BATCH_NUMBER': promo.promoPlanHeader?.batchNumber,
          'STATUS': promo.promoPlanHeader?.workflowStatus?.statusName,
          'NAME': promo.promoPlanHeader?.promoDescription,
          'CHANNEL': promo.promoPlanHeader?.channel,
          'CUSTOMER NAME': promo.promoPlanHeader?.customer?.customerName,
          'CUSTOMER NUMBER': promo.promoPlanHeader?.customer?.customerNumber,
          'BRAND': promo.brand?.brandName,
          'SUB-BRAND': promo.subBrand?.subBrandName,
          'TAMER ITEM No.': promo.tamerItem?.erpItemNumber ?? 'Assorted',
          'CUSTOMER ITEM No.': promo.customerItem?.customerItemNumber ?? 'Assorted',
          'DESCRIPTION': promo.itemDescription,
          'MONTH': promo.promoPlanHeader?.monthYear,
          'PROMO START DATE': promo.promoPlanHeader?.promoStartDate ? new Intl.DateTimeFormat('en-GB').format(new Date(promo.promoPlanHeader.promoStartDate)) : '',
          'PROMO END DATE': promo.promoPlanHeader?.promoEndDate ? new Intl.DateTimeFormat('en-GB').format(new Date(promo.promoPlanHeader.promoEndDate)) : '',
          'PROMO TYPE': promo.promoPlanHeader?.activityType?.name,
          'EXECUTION TYPE': promo.promoPlanHeader?.executionType,
          'CITY': promo.promoPlanHeader?.city,
          'IMS': promo.ims,
          'PTT': promo.ptt,
          'PROMO DESCRIPTION': promo.promoPlanHeader?.promoDescription,
          'DISCOUNT %': promo.discountPercentage,
          'PRICE AFTER DISCOUNT': promo.priceAfterDiscount,
          'COMPENSATION PER UNIT OF SELLOUT': promo.compensationPerUnitOfSellout,
          'ESTIMATED VOLUME (BASED ON HISTORICAL DATA)': promo.estimatedVolume,
          'TOTAL COMPENSATION/FIXED AMOUNT': promo.totalCompensationFixedAmount,
          'CURRENT COST': promo.compensationBasedOnLc,
          'STOCK OH': promo.stockOh,
          'CURRENT RSP': promo.currentRsp,
          'PROMO RRP': promo.promoRrp,
          'NEW COST': promo.newCost,
          'COMMENTS': promo.comments
        }));

        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Promo Plans');
        XLSX.writeFile(wb, 'Promo_Plans_Export.xlsx');
      }
      this.isExporting = false; // Ensure this is always called
    });
  }
}
